/**
 * A tap or click on the scrim closes a modal `<dialog closedby="any">` (cart drawer, quick add,
 * size guide). Chromium and Firefox do that themselves. Safari does not know `closedby` (iOS 26.5
 * and 27.0, 2026-10-05: no `closedBy` on the dialog, and a tap beside the sheet did nothing), so
 * there this module closes the dialog, the way the browser would: as a request the page could
 * cancel. Escape and the close button work natively everywhere.
 *
 * A press on the scrim reports the dialog itself as its target, and so does one on the dialog's
 * own padding, hence the comparison with its box. Both ends of the press have to lie outside: a
 * text selection dragged out of the dialog must not close it. iOS sends `click` only to elements
 * that have a listener of their own, so the listener sits on each dialog, not on the document.
 */
if (!("closedBy" in HTMLDialogElement.prototype)) {
  const armed = new WeakSet();
  let pressed = null;

  const outside = (dialog, event) => {
    const box = dialog.getBoundingClientRect();
    return event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom;
  };

  const arm = (dialog) => {
    if (armed.has(dialog)) return;
    armed.add(dialog);
    dialog.addEventListener("click", (event) => {
      const started = pressed === dialog;
      pressed = null;
      if (started && event.target === dialog && dialog.matches(":modal") && outside(dialog, event)) dialog.requestClose();
    });
  };

  for (const dialog of document.querySelectorAll('dialog[closedby="any"]')) arm(dialog);

  // Where the press began. A dialog that arrived after this module ran (a section rendered again)
  // gets its listener here, in time for the click that follows.
  document.addEventListener(
    "pointerdown",
    (event) => {
      const dialog = event.target;
      pressed = null;
      if (!(dialog instanceof HTMLDialogElement) || !dialog.matches('[closedby="any"]')) return;
      arm(dialog);
      if (outside(dialog, event)) pressed = dialog;
    },
    true,
  );
}
